import { BarChart3, Zap, TrendingUp, Eye, Code, Target, Users, LineChart, Settings, MousePointer, Database, Bell, Shield, ZapOff } from 'lucide-react'

const features = [
  {
    name: 'Análisis de interés en tiempo real',
    description: 'Visualizá el comportamiento de tus visitantes y descubrí qué características generan más interés en cada landing page.',
    icon: BarChart3,
    benefits: [
      'Métricas de engagement por sección',
      'Mapas de calor interactivos',
      'Tiempo de permanencia por característica',
      'Tasa de conversión por funcionalidad'
    ]
  },
  {
    name: 'Integración instantánea',
    description: 'Conectá Validea con tu landing page actual en minutos, sin necesidad de desarrollo complejo o cambios en tu código.',
    icon: Zap,
    benefits: [
      'Snippet de una sola línea',
      'Compatible con cualquier framework',
      'Sin afectar el rendimiento',
      'Configuración en minutos'
    ]
  },
  {
    name: 'Priorización inteligente',
    description: 'Tomá decisiones basadas en datos concretos sobre qué funcionalidades desarrollar primero, maximizando el impacto de tu desarrollo.',
    icon: TrendingUp,
    benefits: [
      'Ranking automático de features',
      'Predicción de impacto',
      'Análisis de ROI por feature',
      'Recomendaciones personalizadas'
    ]
  }
]

const additionalFeatures = [
  {
    name: 'Tracking avanzado',
    description: 'Seguimiento detallado de interacciones y comportamientos',
    icon: Eye,
    details: [
      'Clicks, scrolls y hovers',
      'Tiempo en página por sección',
      'Rutas de navegación',
      'Eventos personalizados'
    ],
    highlight: 'Captura cada interacción',
    color: 'from-blue-500 to-blue-600'
  },
  {
    name: 'APIs flexibles',
    description: 'Integración completa con tu stack tecnológico',
    icon: Code,
    details: [
      'REST API documentada',
      'Webhooks personalizables',
      'SDKs para principales lenguajes',
      'WebSocket para tiempo real'
    ],
    highlight: 'Conecta con tu stack',
    color: 'from-purple-500 to-purple-600'
  },
  {
    name: 'Objetivos personalizados',
    description: 'Define y mide tus KPIs específicos',
    icon: Target,
    details: [
      'Funnels personalizados',
      'Conversiones por feature',
      'Metas por segmento',
      'ROI por característica'
    ],
    highlight: 'Mide lo que importa',
    color: 'from-green-500 to-green-600'
  },
  {
    name: 'Análisis de usuarios',
    description: 'Segmentación y comportamiento por perfil',
    icon: Users,
    details: [
      'Segmentación avanzada',
      'Comportamiento por cohorte',
      'Personas y roles',
      'Feedback contextual'
    ],
    highlight: 'Conoce a tus usuarios',
    color: 'from-pink-500 to-pink-600'
  },
  {
    name: 'Reportes en tiempo real',
    description: 'Dashboards actualizados al instante',
    icon: LineChart,
    details: [
      'Actualización en vivo',
      'Exportación automática',
      'Alertas configurables',
      'Vistas personalizadas'
    ],
    highlight: 'Datos al instante',
    color: 'from-orange-500 to-orange-600'
  },
  {
    name: 'Configuración flexible',
    description: 'Adapta la herramienta a tus necesidades',
    icon: Settings,
    details: [
      'Temas y branding',
      'Reglas de negocio',
      'Workflows personalizados',
      'Integraciones nativas'
    ],
    highlight: 'Adapta a tu proceso',
    color: 'from-indigo-500 to-indigo-600'
  }
]

/**
 * Features section component for the landing page
 * Displays key product benefits with detailed information
 */
export function Features() {
  return (
    <section id="features" className="bg-gray-50 py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Todo lo que necesitás para validar tu idea
          </h2>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            Herramientas poderosas para tomar decisiones basadas en datos reales
          </p>
        </div>

        {/* Main features */}
        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
            {features.map((feature) => (
              <div key={feature.name} className="flex flex-col">
                <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-600">
                    <feature.icon className="h-6 w-6 text-white" aria-hidden="true" />
                  </div>
                  {feature.name}
                </dt>
                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                  <p className="flex-auto">{feature.description}</p>
                  <ul role="list" className="mt-6 space-y-3">
                    {feature.benefits.map((benefit) => (
                      <li key={benefit} className="flex gap-x-3">
                        <div className="h-6 w-6 flex-none rounded-full bg-primary-50 p-1">
                          <div className="h-4 w-4 rounded-full bg-primary-600" />
                        </div>
                        <span className="text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Enhanced additional features grid */}
        <div className="mx-auto mt-32 max-w-2xl sm:mt-40 lg:mt-48 lg:max-w-none">
          <h3 className="text-2xl font-bold tracking-tight text-gray-900 text-center mb-12">
            Y mucho más para potenciar tu validación
          </h3>
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {additionalFeatures.map((feature) => (
              <div
                key={feature.name}
                className="group relative flex flex-col rounded-2xl border border-gray-200 bg-white p-8 shadow-sm transition-all hover:shadow-lg"
              >
                <div className="flex items-center gap-x-4">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r ${feature.color}`}>
                    <feature.icon className="h-6 w-6 text-white" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold leading-7 text-gray-900">
                      {feature.name}
                    </h4>
                    <p className="mt-1 text-sm text-gray-600">
                      {feature.description}
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex-1">
                  <div className="relative">
                    <div className="absolute -left-4 top-0 h-full w-0.5 bg-gradient-to-b from-primary-500 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
                    <ul role="list" className="space-y-3">
                      {feature.details.map((detail) => (
                        <li key={detail} className="flex items-start gap-x-3">
                          <div className="mt-1 h-1.5 w-1.5 flex-none rounded-full bg-primary-600" />
                          <span className="text-sm text-gray-600">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="inline-flex items-center gap-x-2 rounded-full bg-primary-50 px-3 py-1 text-sm font-medium text-primary-600">
                    <span>{feature.highlight}</span>
                    <MousePointer className="h-4 w-4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
} 